import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import moment from 'moment';

import {
  useOrganizationHoldMutation,
  useOrganizationSearchableMutation,
  useOrganizationVatStatusMutation,
  useOrgReviewDecisionMutation,
  useUpdateOrganizationMutation,
} from '@/client/api/admin/organizations';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import type {
  AdminOrganizationDetail,
  AdminOrganizationUpdatePayload,
} from '@/client/types/admin/users-orgs';
import { getApiErrorMessage } from '@/client/utils/errors';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div>
    <span className="block text-xs font-medium uppercase tracking-wide text-gray-500">
      {label}
    </span>
    <span className="text-sm text-gray-900">{value ?? '—'}</span>
  </div>
);

const OverviewTab = ({
  oid,
  org,
}: {
  oid: number;
  org: AdminOrganizationDetail;
}) => {
  const updateMutation = useUpdateOrganizationMutation();
  const holdMutation = useOrganizationHoldMutation();
  const reviewMutation = useOrgReviewDecisionMutation();
  const searchableMutation = useOrganizationSearchableMutation();
  const vatMutation = useOrganizationVatStatusMutation();

  const vatPerm = useHasPermission('system.org_vat.manage');
  const canManageVat = vatPerm.isSuperAdmin || vatPerm.hasPermission;

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<AdminOrganizationUpdatePayload>({
    oid,
    name: org.name,
    description: org.description ?? '',
    photo: typeof org.photo === 'string' ? org.photo : '',
    country: org.country ?? '',
    locale: org.locale ?? '',
    size: org.size ?? undefined,
  });

  const [holdOpen, setHoldOpen] = useState(false);
  const [holdReason, setHoldReason] = useState('');
  const onHold = org.admin_hold === 1;

  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const [searchableOpen, setSearchableOpen] = useState(false);
  const [searchableReason, setSearchableReason] = useState('');
  const isSearchable = org.searchable !== 0;

  const [vatRegistered, setVatRegistered] = useState(org.vat_registered === 1);
  const [vatNumber, setVatNumber] = useState(org.vat_number ?? '');

  useEffect(() => {
    setVatRegistered(org.vat_registered === 1);
    setVatNumber(org.vat_number ?? '');
  }, [org.oid, org.vat_registered, org.vat_number]);

  const startEditing = () => {
    setForm({
      oid,
      name: org.name,
      description: org.description ?? '',
      photo: typeof org.photo === 'string' ? org.photo : '',
      country: org.country ?? '',
      locale: org.locale ?? '',
      size: org.size ?? undefined,
    });
    setEditing(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const toastId = toast.loading('Saving organization…');
    try {
      await updateMutation.mutateAsync(form);
      toast.success('Organization updated', { id: toastId, richColors: true });
      setEditing(false);
    } catch (error: any) {
      toast.error('Failed to update organization', {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    }
  };

  const handleHold = async () => {
    if (!onHold && !holdReason.trim()) return;
    const toastId = toast.loading(onHold ? 'Lifting hold…' : 'Placing hold…');
    try {
      await holdMutation.mutateAsync({
        oid,
        hold: !onHold,
        reason: holdReason.trim() || undefined,
      });
      toast.success(onHold ? 'Hold lifted' : 'Hold placed', {
        id: toastId,
        description: onHold
          ? 'The organization can transact again.'
          : 'The organization can no longer transact.',
        richColors: true,
      });
      setHoldOpen(false);
      setHoldReason('');
    } catch (error: any) {
      toast.error(onHold ? 'Failed to lift hold' : 'Failed to place hold', {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    }
  };

  const handleReview = async (approved: boolean) => {
    if (!approved && !rejectReason.trim()) return;
    const toastId = toast.loading(approved ? 'Approving…' : 'Rejecting…');
    try {
      await reviewMutation.mutateAsync({
        oid,
        approved,
        reason: approved ? undefined : rejectReason.trim(),
      });
      toast.success(approved ? 'Organization approved' : 'Organization rejected', {
        id: toastId,
        description: approved
          ? 'The organization is now publicly listable.'
          : 'It stays in review with the reason recorded.',
        richColors: true,
      });
      setRejectOpen(false);
      setRejectReason('');
    } catch (error: any) {
      toast.error(approved ? 'Failed to approve' : 'Failed to reject', {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    }
  };

  const handleSearchable = async () => {
    if (!searchableReason.trim()) return;
    const next = !isSearchable;
    const toastId = toast.loading(
      next ? 'Making discoverable…' : 'Hiding from directory…'
    );
    try {
      await searchableMutation.mutateAsync({
        oid,
        searchable: next,
        reason: searchableReason.trim(),
      });
      toast.success(next ? 'Now discoverable' : 'Hidden from directory', {
        id: toastId,
        richColors: true,
      });
      setSearchableOpen(false);
      setSearchableReason('');
    } catch (error: any) {
      toast.error('Failed to update directory visibility', {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    }
  };

  const handleVatSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageVat) return;
    const toastId = toast.loading('Saving VAT status…');
    try {
      await vatMutation.mutateAsync({
        oid,
        vat_registered: vatRegistered,
        vat_number: vatRegistered ? vatNumber.trim() || null : null,
      });
      toast.success('VAT status updated', { id: toastId, richColors: true });
    } catch (error: any) {
      toast.error('Failed to update VAT status', {
        id: toastId,
        description: getApiErrorMessage(error, 'Something went wrong.'),
        richColors: true,
        duration: 5000,
      });
    }
  };

  const setField = (key: keyof AdminOrganizationUpdatePayload, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const ownerUid = org.user_account?.uid ?? org.owner_uid ?? org.uid;

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Profile</h2>
            {editing ? (
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            ) : (
              <Button variant="ghost" size="sm" onClick={startEditing}>
                Edit
              </Button>
            )}
          </div>

          {editing ? (
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={form.name ?? ''}
                  onChange={e => setField('name', e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-description">Description</Label>
                <Input
                  id="edit-description"
                  value={form.description ?? ''}
                  onChange={e => setField('description', e.target.value)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <Label htmlFor="edit-country">Country</Label>
                  <Input
                    id="edit-country"
                    value={form.country ?? ''}
                    onChange={e => setField('country', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-locale">Locale</Label>
                  <Input
                    id="edit-locale"
                    value={form.locale ?? ''}
                    onChange={e => setField('locale', e.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="edit-size">Size</Label>
                  <Input
                    id="edit-size"
                    type="number"
                    value={form.size ?? ''}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        size: e.target.value === '' ? undefined : Number(e.target.value),
                      }))
                    }
                  />
                </div>
              </div>
              <div className="space-y-1">
                <Label htmlFor="edit-photo">Photo URL</Label>
                <Input
                  id="edit-photo"
                  value={form.photo ?? ''}
                  onChange={e => setField('photo', e.target.value)}
                />
              </div>
              <p className="text-xs text-gray-500">
                Only whitelisted profile fields are sent. Hold, review, searchable and VAT
                have their own controls.
              </p>
              <Button type="submit" disabled={updateMutation.isPending} className="w-full">
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  'Save changes'
                )}
              </Button>
            </form>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Name" value={org.name} />
              <Field label="Description" value={org.description || '—'} />
              <Field label="Country" value={org.country || '—'} />
              <Field label="Locale" value={org.locale || '—'} />
              <Field label="Size" value={org.size ?? '—'} />
              <Field
                label="Owner"
                value={
                  ownerUid != null ? (
                    <Link
                      to={`/admin/users/${ownerUid}`}
                      className="text-blue-600 hover:underline"
                    >
                      uid {ownerUid}
                      {org.owner_email ? ` · ${org.owner_email}` : ''}
                    </Link>
                  ) : (
                    'No owner'
                  )
                }
              />
              <Field
                label="Created"
                value={
                  org.date_added ? moment(org.date_added).format('YYYY-MM-DD HH:mm') : '—'
                }
              />
              <Field
                label="Modified"
                value={
                  org.date_modified
                    ? moment(org.date_modified).format('YYYY-MM-DD HH:mm')
                    : '—'
                }
              />
              <Field label="Members" value={org.members} />
              <Field label="Listings" value={org.listings} />
            </div>
          )}
        </section>

        {canManageVat && (
          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">VAT status</h2>
            <p className="mt-1 text-xs text-gray-500">
              Requires <code>system.org_vat.manage</code>. Separate from the profile update
              whitelist.
            </p>
            <form onSubmit={handleVatSave} className="mt-4 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={vatRegistered}
                  onChange={e => setVatRegistered(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                VAT registered
              </label>
              <div className="space-y-1">
                <Label htmlFor="vat-number">VAT number</Label>
                <Input
                  id="vat-number"
                  value={vatNumber}
                  disabled={!vatRegistered}
                  onChange={e => setVatNumber(e.target.value)}
                  placeholder="Optional when registered"
                />
              </div>
              <Button type="submit" disabled={vatMutation.isPending}>
                {vatMutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  'Save VAT status'
                )}
              </Button>
            </form>
          </section>
        )}
      </div>

      <div className="space-y-6">
        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Transaction hold</h2>
          <p className="mt-1 text-xs text-gray-500">
            An admin hold stops the organization from transacting. Independent of name
            moderation.
          </p>
          <div className="mt-4 flex items-center gap-2">
            {onHold ? (
              <Badge variant="outline" className="border-red-500/40 bg-red-500/10 text-red-700">
                On hold — cannot transact
              </Badge>
            ) : (
              <Badge variant="secondary">No hold — can transact</Badge>
            )}
          </div>
          {onHold && org.admin_hold_reason && (
            <p className="mt-3 text-sm text-gray-700">
              <span className="font-medium">Reason:</span> {org.admin_hold_reason}
            </p>
          )}
          <Button
            className="mt-4 w-full"
            variant={onHold ? 'outline' : 'destructive'}
            onClick={() => setHoldOpen(true)}
          >
            {onHold ? 'Lift hold' : 'Place hold'}
          </Button>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Name moderation</h2>
          <p className="mt-1 text-xs text-gray-500">
            {org.review === 1
              ? 'Awaiting moderation — not publicly listable. Can still transact unless held.'
              : 'Approved — publicly listable.'}
          </p>
          {org.review_reason && (
            <p className="mt-3 text-sm text-gray-700">
              <span className="font-medium">Last decision:</span> {org.review_reason}
            </p>
          )}
          {org.review === 1 ? (
            <div className="mt-4 flex flex-col gap-2">
              <Button
                className="w-full bg-green-600 text-white hover:bg-green-700"
                disabled={reviewMutation.isPending}
                onClick={() => void handleReview(true)}
              >
                {reviewMutation.isPending ? 'Working…' : 'Approve'}
              </Button>
              <Button
                variant="outline"
                className="w-full text-red-600"
                disabled={reviewMutation.isPending}
                onClick={() => setRejectOpen(true)}
              >
                Reject…
              </Button>
            </div>
          ) : (
            <Button variant="outline" className="mt-4 w-full" asChild>
              <Link to="/admin/organizations/moderation">Open moderation queue</Link>
            </Button>
          )}
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900">Directory visibility</h2>
          <p className="mt-1 text-xs text-gray-500">
            Controls whether the organization appears in directory search. A reason is
            required.
          </p>
          <div className="mt-4">
            {isSearchable ? (
              <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                Discoverable
              </Badge>
            ) : (
              <Badge variant="secondary">Hidden</Badge>
            )}
          </div>
          <Button
            variant="outline"
            className="mt-4 w-full"
            onClick={() => setSearchableOpen(true)}
          >
            {isSearchable ? 'Hide from directory…' : 'Make discoverable…'}
          </Button>
        </section>

        {!canManageVat && org.vat_registered === 1 && (
          <section className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900">VAT</h2>
            <p className="mt-2 text-sm text-gray-700">
              Registered{org.vat_number ? ` · ${org.vat_number}` : ''}
            </p>
            <p className="mt-2 text-xs text-gray-400">
              Editing requires <code>system.org_vat.manage</code>.
            </p>
          </section>
        )}
      </div>

      <AlertDialog
        open={holdOpen}
        onOpenChange={next => {
          if (!next) {
            setHoldOpen(false);
            setHoldReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{onHold ? 'Lift the hold?' : 'Place a hold?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {onHold
                ? 'The organization will be able to transact again. Its review state is unaffected.'
                : 'The organization will be unable to transact. It stays active and its review state is unaffected.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            <Label htmlFor="hold-reason">
              Reason {!onHold && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id="hold-reason"
              value={holdReason}
              onChange={e => setHoldReason(e.target.value)}
              placeholder={onHold ? 'Optional' : 'Why is this organization being held?'}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={holdMutation.isPending || (!onHold && !holdReason.trim())}
              onClick={e => {
                e.preventDefault();
                void handleHold();
              }}
            >
              {holdMutation.isPending ? 'Working…' : onHold ? 'Lift hold' : 'Place hold'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={rejectOpen}
        onOpenChange={next => {
          if (!next) {
            setRejectOpen(false);
            setRejectReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject this organization name?</AlertDialogTitle>
            <AlertDialogDescription>
              It stays in review and is not publicly listable. Rejection does not place a
              hold — the org can still transact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            <Label htmlFor="reject-reason">
              Reason <span className="text-red-500">*</span>
            </Label>
            <Input
              id="reject-reason"
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder="Why is this name being rejected?"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={reviewMutation.isPending || !rejectReason.trim()}
              onClick={e => {
                e.preventDefault();
                void handleReview(false);
              }}
            >
              {reviewMutation.isPending ? 'Working…' : 'Reject'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={searchableOpen}
        onOpenChange={next => {
          if (!next) {
            setSearchableOpen(false);
            setSearchableReason('');
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isSearchable ? 'Hide from the directory?' : 'Make discoverable?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isSearchable
                ? 'The organization will no longer appear in directory search.'
                : 'The organization will appear in directory search again.'}{' '}
              A reason is required and recorded in the audit log.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-1">
            <Label htmlFor="searchable-reason">
              Reason <span className="text-red-500">*</span>
            </Label>
            <Input
              id="searchable-reason"
              value={searchableReason}
              onChange={e => setSearchableReason(e.target.value)}
              placeholder="Recorded in the audit log"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={searchableMutation.isPending || !searchableReason.trim()}
              onClick={e => {
                e.preventDefault();
                void handleSearchable();
              }}
            >
              {searchableMutation.isPending
                ? 'Working…'
                : isSearchable
                  ? 'Hide'
                  : 'Make discoverable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default OverviewTab;
